import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { ImageUpload } from '../../common/ImageUpload';

interface BlogPostFormProps {
  onClose: () => void;
  onSuccess: () => void;
  initialData?: any;
}

export function BlogPostForm({ onClose, onSuccess, initialData }: BlogPostFormProps) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    content: initialData?.content || '',
    excerpt: initialData?.excerpt || '',
    imageUrl: initialData?.imageUrl || '',
    category: initialData?.category || '',
    status: initialData?.status || 'draft',
    isDirty: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState<number | null>(null);

  // Debounced save function
  const debouncedSave = (updates: any) => {
    if (saveTimeout) {
      window.clearTimeout(saveTimeout);
    }
    
    const timeoutId = window.setTimeout(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Authentication required');

        const postData = {
          title: updates.title,
          content: updates.content,
          excerpt: updates.excerpt,
          image_url: updates.imageUrl || updates.image_url,
          category: updates.category,
          status: updates.status,
          author_id: user.id,
          published_at: updates.status === 'published' ? new Date().toISOString() : null
        };

        if (initialData) {
          const { error } = await supabase
            .from('blog_posts')
            .update(postData)
            .eq('id', initialData.id);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('blog_posts')
            .insert([postData]);

          if (error) throw error;
        }

        setFormData(prev => ({ ...prev, isDirty: false }));
        toast.success('Draft saved');
      } catch (error) {
        console.error('Error auto-saving:', error);
        // Don't show error toast for auto-save
      }
    }, 2000); // 2 second debounce

    setSaveTimeout(timeoutId);
  };

  // Handle field changes with debounced save
  const handleFieldChange = (field: string, value: string) => {
    const updates = { ...formData, [field]: value, isDirty: true };
    setFormData(updates);
    
    // Only auto-save if we're editing an existing post
    if (initialData && field !== 'status') {
      debouncedSave(updates);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Authentication required');

      const postData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.excerpt,
        image_url: formData.imageUrl || formData.image_url,
        category: formData.category,
        status: formData.status,
        author_id: user.id,
        published_at: formData.status === 'published' ? new Date().toISOString() : null
      };

      if (initialData) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', initialData.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);

        if (error) throw error;
      }

      toast.success(`Blog post ${initialData ? 'updated' : 'created'} successfully`);
      onSuccess();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error('Failed to save blog post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-black">
            {initialData ? 'Edit Blog Post' : 'Create Blog Post'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Featured Image
            </label>
            <ImageUpload
              onUpload={(url) => setFormData(prev => ({ ...prev, imageUrl: url }))}
              currentImageUrl={formData.imageUrl || formData.image_url}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={e => handleFieldChange('title', e.target.value)}
              className="input w-full text-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Excerpt
            </label>
            <textarea
              value={formData.excerpt}
              onChange={e => handleFieldChange('excerpt', e.target.value)}
              className="input w-full h-20 text-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Content
            </label>
            <textarea
              value={formData.content}
              onChange={e => handleFieldChange('content', e.target.value)}
              className="input w-full h-64 text-black"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={e => handleFieldChange('category', e.target.value)}
                className="input w-full text-black"
              >
                <option value="">Select category</option>
                <option value="Technology">Technology</option>
                <option value="Career Growth">Career Growth</option>
                <option value="Industry Insights">Industry Insights</option>
                <option value="Tutorials">Tutorials</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={e => handleFieldChange('status', e.target.value)}
                className="input w-full text-black"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-primary ${formData.isDirty ? 'opacity-90' : ''}`}
            >
              {isSubmitting ? 'Saving...' : formData.isDirty ? 'Save Changes*' : initialData ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}